import scrollManager from '../utils/scroll-manager';

export default function initArchiveDigitalPlatform() {
    const $archiveDigitalPlatform = document.querySelector<HTMLElement>('.js-page-digital-platform-archive');

    if ($archiveDigitalPlatform) {
        new ArchiveDigitalPlatformPage($archiveDigitalPlatform);
    }
}

export class ArchiveDigitalPlatformPage {
    $container: HTMLElement;
    $resultsContainer: HTMLElement;
    getResultsAbortController: AbortController | null = null;
    resultsCache: {[key: string]: string} = {};
    $searchForm: HTMLFormElement | null;
    $searchFormInput: HTMLInputElement | null;
    $filtersCategory: NodeListOf<HTMLElement>;

    constructor($container: HTMLElement) {
        this.$container = $container;
        this.$resultsContainer = this.$container.querySelector('.js-digital-platform-archive-results') as HTMLElement;
        this.$searchForm = this.$container.querySelector('.js-digital-platform-search-form');
        this.$searchFormInput = this.$container.querySelector('.js-digital-platform-search-form input[name="s"]');
        this.$filtersCategory = this.$container.querySelectorAll('.js-filter-category a');

        // Mise en cache des résultats affichés au chargement de la page
        this.resultsCache[location.href] = document.documentElement.outerHTML;

        // Charger le contenu correspondant lorsque l'utilisateur tente de naviguer
        // dans son historique via les pushState fait préalablement
        addEventListener('popstate', () => {
            this.displayAjaxResults(document.location.href, false);
        });

        // Activation de l'ajax lors de la navigation via la pagination
        document.addEventListener('click', evt => {
            const $target = evt.target as HTMLElement;

            if ($target.closest('.navigation-pagination') && $target instanceof HTMLAnchorElement) {
                evt.preventDefault();

                this.displayAjaxResults($target.href);
            }
        });

        // Formulaire de recherche
        // A la soumission du champ de recherche, on soumet le formulaire
        if (this.$searchForm) {
            this.$searchForm.addEventListener('keypress', (evt: KeyboardEvent) => {
                const $target = evt.target as HTMLElement;
                if ($target === this.$searchFormInput && evt.key === 'Enter') {
                    this.$searchForm?.submit();
                }
            });

            this.$searchForm.addEventListener('submit', (evt: SubmitEvent) => {
                evt.preventDefault();
                this.searchFormSubmit();
            });
        }

        // Click sur filtres catégorie
        this.$filtersCategory.forEach($element => {
            $element.addEventListener('click', evt => {
                this.$container.querySelectorAll('.js-filter-category a.active').forEach($activeElement => {
                    if ($activeElement !== $element) {
                        $activeElement.classList.remove('active');
                    }
                });

                $element.classList.toggle('active');

                if (this.$searchForm) {
                    evt.preventDefault();

                    const newFormAction = $element.classList.contains('active') ? $element.getAttribute('href') : this.$searchForm.getAttribute('data-archive-link');
                    this.$searchForm.setAttribute('action', newFormAction || '');

                    this.searchFormSubmit();
                }
            });
        });
    }

    /**
     * Soumet le formulaire de recherche
     * 
     * @return {void}
     */
    searchFormSubmit() : void {
        if (!this.$searchForm) {
            return;
        }

        // Construction de l'url
        const url = new URL((this.$searchForm.getAttribute('action') ?? location.origin + location.pathname));
        const formData = new FormData(this.$searchForm);

        formData.forEach((value: FormDataEntryValue, key: string) => {
            if (!(value instanceof File) && value) {
                url.searchParams.append(key, value);
            }
        });

        this.displayAjaxResults(url.toString());
    }

    /**
     * Demande au serveur les résultats
     * 
     * @param {string} url
     * @param {boolean} withPushState
     * 
     * @return {void}
     */
    displayAjaxResults(url: string, withPushState = true) : void {
        // Annulation de la requête précédente
        if (this.getResultsAbortController) {
            this.getResultsAbortController.abort();
        }

        if (withPushState) {
            // Changement de l'URL
            window.history.pushState(null, '', url);
        }

        if (typeof this.resultsCache[url] !== 'undefined') {
            this.displayResults(url, this.resultsCache[url]);
            return;
        }

        this.getResultsAbortController = new AbortController();

        this.$resultsContainer.classList.add('loading');

        fetch(url, {
            method: 'GET',
            credentials: 'same-origin',
            signal: this.getResultsAbortController.signal,
        }).then(response => {
            return response.text();
        }).then(content => {
            this.displayResults(url, content);

            this.$resultsContainer.classList.remove('loading');
        }).catch(() => {
            this.$resultsContainer.classList.remove('loading');
        });
    }

    /**
     * Affiche les résultats dans le DOM
     * 
     * @param {string} url
     * @param {string} content
     * 
     * @return {void}
     */
    displayResults(url: string, content: string) : void {
        // Récupération du HTML contenant les résultats
        const div = document.createElement('div');
        div.innerHTML = content;
        const resultsHTML = (div.querySelector('.js-digital-platform-archive-results') as HTMLElement).innerHTML;

        // Mise en cache du résultat
        if (typeof this.resultsCache[url] === 'undefined') {
            this.resultsCache[url] = content;
        }

        // Ajout des résultats dans le DOM
        this.$resultsContainer.innerHTML = resultsHTML;

        // Scroll jusqu'à la barre de recherche
        const adminBarHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--admin-bar-height'));
        const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'));
        const scrollOffset = adminBarHeight + headerHeight + 50;
        scrollManager.scrollTo((this.$container as HTMLElement), true, scrollOffset);
    }
}